import { notNull } from "./helpers";

export type IpLocalization = {
  ipCountry?: string;
  ipTimeZone?: string;
};

export type DeviceLocalization = {
  locales?: readonly string[];
  // Regions parsed from the browser's locales, preference-ordered.
  localeRegions?: readonly string[];
  timeZone?: string;
};

export type LocalizationSignals = IpLocalization & DeviceLocalization;

export const LOCALIZATION_REGIONS = ["europe"] as const;

export type LocalizationRegion = (typeof LOCALIZATION_REGIONS)[number];

type LocalizationRegionRule = {
  region: LocalizationRegion;
  countries: readonly string[];
  timeZonePrefixes: readonly string[];
};

// First match wins.
const LOCALIZATION_REGION_RULES: readonly LocalizationRegionRule[] = [
  {
    region: "europe",
    countries: ["GB", "GG", "IM", "JE"],
    timeZonePrefixes: ["Europe/"],
  },
];

export const isLocalizationRegion = (
  value: unknown
): value is LocalizationRegion =>
  LOCALIZATION_REGIONS.some((region) => region === value);

const matchesCountry = (
  { countries }: LocalizationRegionRule,
  country: string | undefined
) => notNull(country) && countries.includes(country);

const matchesTimeZone = (
  { timeZonePrefixes }: LocalizationRegionRule,
  timeZone: string | undefined
) =>
  notNull(timeZone) &&
  timeZonePrefixes.some((prefix) => timeZone.startsWith(prefix));

// Tiers ordered by signal reliability; any affirmative match wins (greedy).
export const getLocalizationRegion = ({
  localeRegions = [],
  timeZone,
  ipCountry,
  ipTimeZone,
}: LocalizationSignals): LocalizationRegion | null =>
  (
    LOCALIZATION_REGION_RULES.find((rule) =>
      localeRegions.some((country) => matchesCountry(rule, country))
    ) ??
    LOCALIZATION_REGION_RULES.find((rule) => matchesTimeZone(rule, timeZone)) ??
    LOCALIZATION_REGION_RULES.find((rule) => matchesCountry(rule, ipCountry)) ??
    LOCALIZATION_REGION_RULES.find((rule) => matchesTimeZone(rule, ipTimeZone))
  )?.region ?? null;
